// Pure helpers for the Lead Agent pre-acceptance banner (RET-1230 / C2).
// Kept out of the component for testability and the
// react-refresh/only-export-components rule.

import moment from 'moment';
// Register the es-MX locale data we render in user-facing dates. Use the
// ESM-built dist path, never `moment/locale/...` (Vite's dep optimizer drops
// the UMD side-effect import and the format silently falls back to English).
// See docs/solutions/integration-issues/moment-locale-umd-dropped-by-vite.md.
import 'moment/dist/locale/es-mx';
import { RouterPaths } from 'src/components/Router/Router.constants';
import {
  ePlanCode,
  ePlanFrequency,
} from 'src/providers/SubscriptionProvider/Subscription.enums';
import {
  mapBannerColor,
  mapPaywallPeriodicity,
  mapPaywallPlan,
} from 'src/hooks/analytics/leadAgentPaywallAnalytics.constants';
import type {
  tLeadAgentBannerViewedProps,
  tLeadAgentPaywallSurface,
} from 'src/hooks/analytics/leadAgentPaywallAnalytics.constants';
import type {
  tLeadAgentBannerVariant,
  tLeadAgentSelectableTier,
  tLeadAgentWorkspaceBucket,
} from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.types';

/** Full window (in days) the urgency timebar progresses across. */
export const LEAD_AGENT_TIMEBAR_WINDOW_DAYS = 14;
const TIMEBAR_MIN_FILL = 5;
const TIMEBAR_MAX_FILL = 95;

/** The banner's size depends on the surface; Home is large, the rest compact. */
export type tLeadAgentBannerSize = 'large' | 'compact';

/**
 * Formats a metric integer for display. Returns the rounded value as a string;
 * `null`/invalid yields null so the caller can hide the whole card instead of
 * rendering a misleading zero.
 */
export function formatMetricValue(value: number | null): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.round(value).toLocaleString('es-MX');
}

/**
 * Shared es-MX formatter for the personal next-payment / first-charge date.
 * Renders in the VIEWER's local timezone (moment defaults to local mode): the
 * banner shows the moment the trial ends / next charge is due on the customer's
 * OWN clock, not a fixed reference zone.
 *
 * RET-1564: next_payment_at now encodes 23:59:59 America/Mexico_City of the
 * billing day (= 05:59:59 UTC the next day). Rendered locally, a viewer in
 * Mexico sees billing day D; a viewer east of Mexico sees that end-of-day
 * boundary fall in their early-morning D+1 — which is exactly when their access
 * ends in their own time. Do NOT parse in UTC (`moment.utc`): that pinned every
 * viewer to the UTC calendar day and, post-RET-1564, showed the date one day
 * late for everyone (including Mexico). Returns null for missing/invalid dates
 * so callers can degrade their copy gracefully.
 */
function formatPaymentDateWith(
  nextPaymentAt: string | null,
  format: string,
): string | null {
  if (!nextPaymentAt) return null;
  const date = moment(nextPaymentAt);
  if (!date.isValid()) return null;
  return date.locale('es-mx').format(format);
}

/**
 * Personal first-charge / renewal date formatted in es-MX as "D [de] MMM YYYY"
 * (e.g. "10 de jun 2026", abbreviated month). Used by the modal terms/legend.
 */
export function formatPaymentDate(nextPaymentAt: string | null): string | null {
  // es-MX renders the abbreviated month with a trailing period (e.g. "jun.").
  return formatPaymentDateWith(nextPaymentAt, 'D [de] MMM YYYY');
}

/**
 * Day + abbreviated month WITHOUT the year, e.g. "1 de ago." — for dates that are
 * unambiguously within the current period (the cap-burn projection can never be
 * more than a month out), where the year is noise that wraps a narrow column.
 */
export function formatPaymentDateShort(
  nextPaymentAt: string | null,
): string | null {
  return formatPaymentDateWith(nextPaymentAt, 'D [de] MMM');
}

/**
 * Same date formatted with the full month for the banner reassure copy, e.g.
 * "27 de junio de 2026" (RET-1288). Returns null for missing/invalid dates so
 * the copy falls back to the no-date variant.
 */
export function formatPaymentDateLong(
  nextPaymentAt: string | null,
): string | null {
  return formatPaymentDateWith(nextPaymentAt, 'D [de] MMMM [de] YYYY');
}

/**
 * Progressive fill percent of the urgency timebar within the 14-day window:
 * the closer to the charge date, the fuller. clamp(((14 - days) / 14) * 100,
 * 5, 95). Null/unknown days yields the minimum fill (window just started).
 */
export function computeTimebarFillPercent(days: number | null): number {
  if (typeof days !== 'number' || !Number.isFinite(days)) {
    return TIMEBAR_MIN_FILL;
  }
  const raw =
    ((LEAD_AGENT_TIMEBAR_WINDOW_DAYS - days) / LEAD_AGENT_TIMEBAR_WINDOW_DAYS) *
    100;
  return Math.min(Math.max(raw, TIMEBAR_MIN_FILL), TIMEBAR_MAX_FILL);
}

/**
 * Maps a route pathname to the acceptance surface, or null when the banner
 * should not render there. Home = dashboard, funnels = embudos,
 * automations = leadbot (and nested routes).
 */
export function resolveSurface(
  pathname: string,
): tLeadAgentPaywallSurface | null {
  if (pathname.startsWith(RouterPaths.dashboard)) return 'home';
  if (pathname.startsWith(RouterPaths.funnels)) return 'funnels';
  if (pathname.startsWith(RouterPaths.leadbot)) return 'automations';
  return null;
}

/** Large on Home, compact on funnels/automations (RET-1230 R8). */
export function resolveBannerSize(
  surface: tLeadAgentPaywallSurface,
): tLeadAgentBannerSize {
  return surface === 'home' ? 'large' : 'compact';
}

export type tBuildBannerViewedPropsArgs = {
  variant: tLeadAgentBannerVariant;
  daysUntilPayment: number | null;
  planCode: ePlanCode | null | undefined;
  frequency: ePlanFrequency | null | undefined;
  suggestedTier: tLeadAgentSelectableTier;
  surface: tLeadAgentPaywallSurface;
  workspaceBucket: tLeadAgentWorkspaceBucket | null;
};

/**
 * Assembles the `la_paywall_banner_viewed` props (D8 plan). `workspace_bucket`
 * is only included when the cohort bucket is known (omitted otherwise).
 */
export function buildBannerViewedProps(
  args: tBuildBannerViewedPropsArgs,
): tLeadAgentBannerViewedProps {
  const props: tLeadAgentBannerViewedProps = {
    banner_color: mapBannerColor(args.variant),
    days_until_paywall: args.daysUntilPayment,
    plan: mapPaywallPlan(args.planCode),
    plan_periodicity: mapPaywallPeriodicity(args.frequency),
    suggested_tier:
      args.suggestedTier.toLowerCase() as Lowercase<tLeadAgentSelectableTier>,
    surface: args.surface,
  };
  if (args.workspaceBucket) {
    props.workspace_bucket = args.workspaceBucket;
  }
  return props;
}

/** sessionStorage key for the once-per-(workspace+session) viewed dedupe. */
export function bannerViewedDedupeKey(workspaceId: string): string {
  return `la_paywall_banner_viewed:${workspaceId}`;
}
